import { FreshStartOrigin } from "./FreshStartOriginConfig";

export enum AuthFlowType {
  EMAIL_LOGIN = 'email-login',
  EMAIL_LOGIN_PHONE_NUMBER_ASSOCIATION = 'email-login-phone-number-association',
  PHONE_LOGIN = 'phone-login',
  PHONE_LOGIN_EMAIL_ASSOCIATION = 'phone-login-email-association',
  PHONE_REGISTRATION_EMAIL_ASSOCIATION = 'phone-registration-email-association',
  EMAIL_REGISTRATION_PHONE_ASSOCIATION = 'email-registration-phone-association',
  SRT = 'srt',
  SITE_CREDENTIALS_PW = 'site-credentials_pw',
  SITE_CREDENTIALS_PHONE = 'site-credentials_phone',
  SITE_CREDENTIALS_EMAIL = 'site-credentials_email',
  SITE_CREDENTIALS_CONSENTS = 'site-credentials_consents'
}

export enum FlowType {
  AUTH = 'auth',
  ASSOCIATION = 'association',
  SITE_CREDENTIALS = 'site-credentials',
}

export interface FlowConfig {
  form: string;
  flow: AuthFlowType;
  type: FlowType,
  labels: {
    headerTitle: string;
    headerSubtitle?: string;
    input: string;
    inputAria: {
      required: string;
      notRequired?: string;
    }
    nextButton: string;
    skipButton?: string;
  }
  identifierField?: 'email' | 'phoneNumber' | 'username';
  useCaptcha?: boolean;
  showBackButton?: boolean;
  showForgotPassword?: boolean;
  showFreshStart?: boolean;
  freshStartOrigin?: FreshStartOrigin
}

export const FLOW_CONFIGS: Record<AuthFlowType, FlowConfig> = {
  [AuthFlowType.EMAIL_LOGIN]: {
    form: "validate_password__" + AuthFlowType.EMAIL_LOGIN,
    flow: AuthFlowType.EMAIL_LOGIN,
    type: FlowType.AUTH,
    labels: {
      headerTitle: "login_password_associated.header_title",
      headerSubtitle: "login_password_associated.header_message",
      input: 'login_password_associated.password_input',
      inputAria: {
        required: 'accessibility.email.login.validate_password.input'
      },
      nextButton: 'password_definition.button_next_message'
    },
    identifierField: 'email',
    showForgotPassword: true
  },
  [AuthFlowType.EMAIL_LOGIN_PHONE_NUMBER_ASSOCIATION]: {
    form: "validate_password__" + AuthFlowType.EMAIL_LOGIN_PHONE_NUMBER_ASSOCIATION,
    flow: AuthFlowType.EMAIL_LOGIN_PHONE_NUMBER_ASSOCIATION,
    type: FlowType.ASSOCIATION,
    labels: {
      headerTitle: "email_login_phonenumber_association_password.header_title",
      headerSubtitle: "email_login_phonenumber_association_password.header_message",
      input: 'email_login_phonenumber_association_password.password_input',
      inputAria: {
        required: 'accessibility.email.login.validate_password_phonenumber_association.input_required',
        notRequired: 'accessibility.email.login.validate_password_phonenumber_association.input'
      },
      skipButton: 'phonenumber_association.jump_next_button_message',
      nextButton: 'email_login_phonenumber_association_password.button_next_message'
    },
    identifierField: 'phoneNumber',
    showBackButton: true,
    showForgotPassword: true
  },
  [AuthFlowType.PHONE_LOGIN_EMAIL_ASSOCIATION]: {
    form: "validate_password__" + AuthFlowType.PHONE_LOGIN_EMAIL_ASSOCIATION,
    flow: AuthFlowType.PHONE_LOGIN_EMAIL_ASSOCIATION,
    type: FlowType.ASSOCIATION,
    labels: {
      headerTitle: "login_phoneNumber_email_password_validation.header_title",
      headerSubtitle: "login_phoneNumber_email_password_validation.header_message",
      input: 'login_phoneNumber_email_password_validation.password_input',
      inputAria: {
        required: 'accessibility.phonenumber.login.validate_password_email_association.input.required',
        notRequired: 'accessibility.phonenumber.login.validate_password_email_association.input.not_required'
      },
      skipButton: 'email_association.jump_next_button_message',
      nextButton: 'login_phoneNumber_email_password_validation.button_next_message'
    },
    identifierField: 'email',
    showBackButton: true,
    showForgotPassword: true
  },
  [AuthFlowType.PHONE_LOGIN]: {
    form: "validate_password__" + AuthFlowType.PHONE_LOGIN,
    flow: AuthFlowType.PHONE_LOGIN,
    type: FlowType.AUTH,
    labels: {
      headerTitle: "phonenumber_login_password_associated.header_title",
      headerSubtitle: "phonenumber_login_password_associated.header_message",
      input: 'phonenumber_login_password_associated.password_input',
      inputAria: {
        required: 'accessibility.phonenumber.login.validate_password.input'
      },
      nextButton: 'phonenumber_login_password_associated.button_next_message'
    },
    identifierField: 'phoneNumber',
    showBackButton: true,
    showForgotPassword: true,
    showFreshStart: true,
    freshStartOrigin: FreshStartOrigin.PHONE_LOGIN_PASSWORD,
    useCaptcha: true
  },
  [AuthFlowType.EMAIL_REGISTRATION_PHONE_ASSOCIATION]: {
    form: "validate_password__" + AuthFlowType.EMAIL_REGISTRATION_PHONE_ASSOCIATION,
    flow: AuthFlowType.EMAIL_REGISTRATION_PHONE_ASSOCIATION,
    type: FlowType.ASSOCIATION,
    labels: {
      headerTitle: "phonenumber_login_password_associated.header_title",
      headerSubtitle: "phonenumber_login_password_associated.header_message",
      input: 'phonenumber_login_password_associated.password_input',
      inputAria: {
        required: 'accessibility.email.registration.verify_password.input_required',
        notRequired: 'accessibility.email.registration.verify_password.input'
      },
      nextButton: 'password_definition.button_next_message'
    },
    identifierField: 'phoneNumber',
    useCaptcha: true,
    showBackButton: true,
    showForgotPassword: true
  },
  [AuthFlowType.PHONE_REGISTRATION_EMAIL_ASSOCIATION]: {
    form: "validate_password__" + AuthFlowType.PHONE_REGISTRATION_EMAIL_ASSOCIATION,
    flow: AuthFlowType.PHONE_REGISTRATION_EMAIL_ASSOCIATION,
    type: FlowType.ASSOCIATION,
    labels: {
      headerTitle: "login_password_associated.header_title",
      headerSubtitle: "login_password_associated.header_message",
      input: 'login_password_associated.password_input',
      inputAria: {
        required: 'accessibility.phonenumber.registration.validate_password.input.required',
        notRequired: 'accessibility.phonenumber.registration.validate_password.input.not_required'
      },
      skipButton: 'email_association.jump_next_button_message',
      nextButton: 'password_definition.button_next_message'
    },
    identifierField: 'email',
    showBackButton: true,
    showForgotPassword: true
  },
  [AuthFlowType.SRT]: {
    form: "validate_password__" + AuthFlowType.SRT,
    flow: AuthFlowType.SRT,
    type: FlowType.AUTH,
    labels: {
      headerTitle: "srt.password_validation.header_title",
      headerSubtitle: "srt.password_validation.header_message",
      input: 'login_password_associated.password_input',
      inputAria: {
        required: 'accessibility.phonenumber.registration.validate_password.input.required',
        notRequired: 'accessibility.phonenumber.registration.validate_password.input.not_required'
      },
      skipButton: 'email_association.jump_next_button_message',
      nextButton: 'password_definition.button_next_message'
    },
    identifierField: 'username',
    useCaptcha: true,
    showBackButton: false,
    showForgotPassword: true
  },
  [AuthFlowType.SITE_CREDENTIALS_PW]: {
    form: "validate_password__" + AuthFlowType.SITE_CREDENTIALS_PW,
    flow: AuthFlowType.SITE_CREDENTIALS_PW,
    type: FlowType.SITE_CREDENTIALS,
    labels: {
      headerTitle: "site_credentials.update_password.validate_password.title",
      headerSubtitle: "site_credentials.update_password.validate_password.subtitle",
      input: 'site_credentials.update_password.validate_password.password',
      inputAria: {
        required: 'site_credentials.update_password.validate_password.password'
      },
      nextButton: 'password_definition.button_next_message'
    },
    useCaptcha: false,
    showBackButton: true,
    showForgotPassword: false
  },
  [AuthFlowType.SITE_CREDENTIALS_PHONE]: {
    form: "validate_password__" + AuthFlowType.SITE_CREDENTIALS_PHONE,
    flow: AuthFlowType.SITE_CREDENTIALS_PHONE,
    type: FlowType.SITE_CREDENTIALS,
    labels: {
      headerTitle: "site_credentials.phonenumber_association.validate_password.title",
      headerSubtitle: "site_credentials.phonenumber_association.validate_password.subtitle",
      input: 'site_credentials.phonenumber_association.validate_password.password',
      inputAria: {
        required: 'site_credentials.phonenumber_association.validate_password.password'
      },
      nextButton: 'password_definition.button_next_message'
    },
    useCaptcha: false,
    showBackButton: true,
    showForgotPassword: false
  },
  [AuthFlowType.SITE_CREDENTIALS_EMAIL]: {
    form: "validate_password__" + AuthFlowType.SITE_CREDENTIALS_EMAIL,
    flow: AuthFlowType.SITE_CREDENTIALS_EMAIL,
    type: FlowType.SITE_CREDENTIALS,
    labels: {
      headerTitle: "site_credentials.email_association.validate_password.title",
      headerSubtitle: "site_credentials.email_association.validate_password.subtitle",
      input: 'site_credentials.email_association.validate_password.password',
      inputAria: {
        required: 'site_credentials.phonenumber_association.validate_password.password'
      },
      nextButton: 'password_definition.button_next_message'
    },
    useCaptcha: false,
    showBackButton: true,
    showForgotPassword: false
  },
  [AuthFlowType.SITE_CREDENTIALS_CONSENTS]: {
    form: "validate_password__" + AuthFlowType.SITE_CREDENTIALS_CONSENTS,
    flow: AuthFlowType.SITE_CREDENTIALS_CONSENTS,
    type: FlowType.SITE_CREDENTIALS,
    labels: {
      headerTitle: "site_credentials.consents.validate_password.title",
      headerSubtitle: "site_credentials.consents.validate_password.subtitle",
      input: 'site_credentials.consents.validate_password.password',
      inputAria: {
        required: 'site_credentials.consents.validate_password.password'
      },
      nextButton: 'password_definition.button_next_message'
    },
    useCaptcha: false,
    showBackButton: true,
    showForgotPassword: true
  },
};